import Accordion from "@/common/components/Accordion"
import Box from "@/common/components/Box"
import Button from "@/common/components/Button"
import Checkbox from "@/common/components/Checkbox"
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper"
import { ModalErrorMessage } from "@/common/components/ModalErrorMessage"
import TextField from "@/common/components/TextField"
import { ErrorResponse } from "@/common/types/errorResponse"
import { formatCurrency, formatPercentage } from "@/common/utils/formatters"
import { ModalDeleteFeeTemplate } from "@/features/feeTemplates/components/ModalDeleteFeeTemplate"
import { ChevronsRight } from "lucide-react"
import { useState } from "react"
import { Link, useParams } from "react-router-dom"
import { twMerge } from "tailwind-merge"
import { strategy as applicableEntityStrategy } from "../../constants/applicableEntity.constant"
import { strategy as calculateAsStrategy } from "../../constants/calculateAs.constant"
import { strategy as feeTemplateKindStrategy } from "../../constants/feeTemplateKind.constant"
import { strategy as paymentModalityStrategy } from "../../constants/paymentModality.constant"
import useFeeTemplate from "../../hooks/useFeeTemplate"
import GoBackButton from "@/common/components/GoBackButton";
import { applicableEstablishmentCommercialKind } from "../../constants/applicableEstablishmentCommercialKind.constant"

const Show = () => {
  const { id } = useParams()

  const [errorData, setErrorData] = useState<ErrorResponse | boolean>(false)

  const {
    data: feeTemplate,
    isPending: feeTemplateIsPending,
    error: feeTemplateError = {} as any,
    refetch: refetchFeeTemplate,
  } = useFeeTemplate(id || "")

  return (
    <ErrorMessageWrapper
      className="max-h-[calc(100vh-64px)]"
      statusCode={feeTemplateError?.status}
      refetchFunction={refetchFeeTemplate}
    >
      <div>
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback="/fee-templates" />
            <p className="text-white-400 font-normal">Tarifas</p>
            <ChevronsRight color="#ffffffb3" />
            <h2>{feeTemplate?.display_name}</h2>
          </div>
          <div
            className={twMerge(
              "flex items-center gap-x-4",
              feeTemplateIsPending || feeTemplateError?.status === 403 ? "hidden" : "",
            )}
          >
            <ModalDeleteFeeTemplate operationKlassToCheckPermission="Admin::Billing::FeeTemplates::Destroy" feeTemplateId={id} onError={setErrorData} />
            <Link to={`/fee-templates/${id}/edit`}>
              <Button operationKlassToCheckPermission="Admin::Billing::FeeTemplates::Update" data-testid="access_fee_templates_show_button" type="submit" className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
                Editar
              </Button>
            </Link>
          </div>
        </div>

        <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] max-h-[calc(100vh-128px)] overflow-y-auto">
          <p className="text-lg font-bold">{feeTemplate?.display_name}</p>
          <TextField label="Nome" data-testid="display_name_input" disabled value={feeTemplate?.display_name} />
          <Checkbox
            label="Tarifa padrão (adicionada em todas condições comerciais para esse tipo de cliente)"
            labelClassName="italic text-xs"
            name="default_value"
            checked={feeTemplate?.apply_by_default}
          />
          <Checkbox
            label="Não aplicar quando houver tarifa especial"
            labelClassName="italic text-xs"
            name="not_use_if_has_special_fee"
            checked={feeTemplate?.not_use_if_has_special_fee}
          />
          <div className="flex gap-2">
            <TextField
              label="Tipo de tarifa"
              data-testid="kind_selector"
              disabled
              className="w-1/4"
              value={feeTemplateKindStrategy[feeTemplate?.kind as keyof typeof feeTemplateKindStrategy]}
            />
            <TextField
              label="Aplicado em"
              data-testid="applicable_entity_selector"
              disabled
              className="w-1/4"
              value={applicableEntityStrategy[feeTemplate?.applicable_entity as keyof typeof applicableEntityStrategy]}
            />
            <TextField
              label="Modalidade de pagamento"
              data-testid='payment_modality_selector'
              disabled
              className="w-1/4"
              value={paymentModalityStrategy[feeTemplate?.payment_modality as keyof typeof paymentModalityStrategy]}
            />
            <TextField
              label="Medida"
              data-testid='calculate_as_selector'
              disabled
              className="w-1/4"
              value={calculateAsStrategy[feeTemplate?.calculate_as as keyof typeof calculateAsStrategy]}
            />
            <TextField
              label="Valor padrão"
              data-testid="default_value_input"
              disabled
              className="w-1/4"
              value={
                feeTemplate?.calculate_as === "percentage"
                  ? formatPercentage(feeTemplate?.default_value || 0)
                  : formatCurrency(feeTemplate?.default_value || 0)
              }
            />
          </div>
          <TextField label="Aplicação" data-testid='option_id_selector' disabled value={feeTemplate?.option?.display_name} />

          <TextField
            label="Tipo de estabelecimento"
            data-testid='applicable_establishment_commercial_kinds'
            disabled
            value={
              feeTemplate?.applicable_establishment_commercial_kinds
                ? applicableEstablishmentCommercialKind[feeTemplate?.applicable_establishment_commercial_kinds]
                : 'Não informado'
            }
          />

          <Accordion
            head={
              <Checkbox
                label="Consumo mínimo"
                checked={!!feeTemplate?.conditional_minimum_consumption}
                disabled
                name="mininalConsumption"
              />
            }
            body={
              <TextField
                label="Consumo mínimo"
                disabled
                value={
                  feeTemplate?.conditional_minimum_consumption
                    ? formatCurrency(feeTemplate?.conditional_minimum_consumption)
                    : undefined
                }
              />
            }
          />
          <Accordion
            head={
              <Checkbox
                label="Produtos e serviços aplicáveis"
                checked={!!feeTemplate?.fee_template_applicable_products?.length}
                disabled
                name="applicableProducts"
              />
            }
            body={
              <TextField
                label="Produtos e serviços aplicáveis"
                disabled
                value={feeTemplate?.fee_template_applicable_products
                  ?.map((product) => product.applicable.display_name)
                  ?.join(", ")}
              />
            }
          />
          <Accordion
            name="fee_template_applicable_capture_methods"
            head={
              <Checkbox
                label="Métodos de capturas aplicáveis"
                checked={!!feeTemplate?.fee_template_applicable_capture_methods?.length}
                disabled
                name="applicableCaptureMethods"
              />
            }
            body={
              <TextField
                name="fee_template_applicable_capture_methods"
                label="Métodos de capturas aplicáveis"
                disabled
                value={feeTemplate?.fee_template_applicable_capture_methods
                  ?.map(({ capture_method }) => capture_method.display_name)
                  ?.join(", ")}
              />
            }
          />
        </Box>

        <ModalErrorMessage
          data={errorData as ErrorResponse}
          setData={setErrorData as React.Dispatch<React.SetStateAction<boolean>>}
          title="Deleção não concluída"
          message="Algo deu errado e a deleção não foi concluída."
        />
      </div>
    </ErrorMessageWrapper>
  )
}

export default Show
